import * as React from "react";

import { subCategoryAll } from '../products/FilteredProductsProvider'; 
import { fetchProductsByCategory, mapByDispensary } from './category-search-utils';
import { trackEvent } from '../analytics/tracking';

export const ShopByProductContext = React.createContext({});
/**
 *  This provider is used for the Shop By Product filtered products view
 * 
 * @param {string} initialCategory - The initial category (from URL ) or default.
 * @param {string} userAddress - The address object (if available) from the UserProvider
 * @param {boolean} isPickup - The id of the current dispensary 
 */
const ShopByProductProvider = ({
  initialCategory,
  userAddress,
  isPickup, 
  children}) => {

  // Map (object) of dispensaries to category products 
  const [categoryProductsByDispensary, setCategoryProductsByDispensary] = React.useState({});
  // Similar map containing currently filtered products (not implemented in UI)
  const [filteredProductsByDispensary, setFilteredProductsByDispensary] = React.useState({}); 
  const [loading, setLoading] = React.useState();

  // State for currently filtered products
  const [currentCategory, setCurrentCategory] = React.useState();
  const [requestedCategory, setRequestedCategory] = React.useState(initialCategory);
  
  // subCategories are objects!
  const [currentSubCategories, setCurrentSubCategories] = React.useState([]);
  const [currentSubCategory, setCurrentSubCategory] = React.useState(subCategoryAll);

  // Create a sorted artray of subCategory objects (on init)
  const updateSubCategories = (categoryProducts) => {
    // Map of subCategory name to full object ( display_name etc.)
    const subCategoryMap = categoryProducts.reduce((nameToObjMap, product) => {
      const name = product.display_info.sub_category?.name; 
      if (name) {
        nameToObjMap.set(name, product.display_info.sub_category);
      }
      return nameToObjMap;
    }, new Map());
    // Sort alphabetically
    const subCatArray =  Array.from(subCategoryMap.values());
    if (subCatArray.length) {
      subCatArray.sort((a, b) => a.display_name.localeCompare(b.display_name));
    } 
    setCurrentSubCategories(subCatArray);
  };

  // Build the unique brands/types/etc. for a subCategory 
  /** Unused but keeping for brand/type implementation
  const buildFilterItems = (category, productDetail, productMap) => {
    const categoryProducts = productMap[category] || [];
    // build the brand set
    const detailSet = categoryProducts.reduce((unique, product) => {
      if (product.display_info[productDetail]) {
        unique.add(product.display_info[productDetail]);
      }
      return unique;
    }, new Set());
    return Array.from(detailSet).sort(); 
  };
  */
  
  // Products fetch callback
  const callback = React.useCallback((response) => {
    console.log('ShopByProductProvider callback!');
    // productsByDispensary is an object
    updateSubCategories([ ...response.data]);
    // This method mutates the array
    const productsByDispensary = mapByDispensary([ ...response.data]);
    // Sort featured products (.is_featured) first!
    Object.keys(productsByDispensary).forEach(dispensaryid => {
      productsByDispensary[dispensaryid].sort((a,b,) => {
        return a.is_featured === b.is_featured ? 0 : a.is_featured? -1 : 1;
      })
    });  
    setCategoryProductsByDispensary(productsByDispensary);
    setCurrentCategory(requestedCategory);
    setLoading(false);
  }, [requestedCategory]);

  // Handle initial product data fetch
  React.useEffect(() => {
    const controller = new AbortController();
    // Fetch products by category
    if (requestedCategory !== currentCategory) {
      setLoading(true);
      try {
        // scope by zip code for DELIVERY ONLY
        const optionalZipCode = isPickup ? null : userAddress ? userAddress.zip_code : null;
        fetchProductsByCategory(requestedCategory, optionalZipCode, (results) => {callback(results, currentCategory)}, controller);
      } catch(error) {
        trackEvent('error_fetching_products_by_category');
      }
    }  
    return () => controller.abort();
  }, [requestedCategory, currentCategory, callback, isPickup, userAddress]);

  // Updating category will trigger a fetch  
  const setCategory = (category) => {
    if (category !== currentCategory) {
      setRequestedCategory(category.toLowerCase());
    }
  };

  // Filters are used against previously fetched products
  const setFilters = (filters) => {
    // Accept filter values using current for missing options
    const { 
      subCategory=currentSubCategory  /* object */
    } = filters;
    
    // Handle subcategory
    if ( subCategory.name !== currentSubCategory.name ) {
      const subCategoryProducts = [];
      // loop thru dispensaries and grab first 4 matching subCategory products 
      Object.keys(categoryProductsByDispensary).forEach((key, idx) => {
        const products = categoryProductsByDispensary[key];
        while (subCategoryProducts.length < 4 && idx < products.length) {
          if (products[idx].display_info.sub_category?.name) {
            subCategoryProducts.push(products[idx]);
          }
        }     
      });
      setCurrentSubCategory(subCategory)
      setFilteredProductsByDispensary(subCategoryProducts);
    } 
  };
 
  return (
    <ShopByProductContext.Provider value={{
      categoryProductsByDispensary,
      category: currentCategory,
      setCategory,
      filteredProductsByDispensary,
      setFilters,  /* currently subCategory only */ 
      subCategories: currentSubCategories,
      subCategory: currentSubCategory,
      loading
    }}>
      {children}
    </ShopByProductContext.Provider>
  );
};

export default ShopByProductProvider;